import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheckCircle,
  FiClock,
  FiEdit3,
  FiHelpCircle,
  FiLayers,
  FiPlay,
  FiTarget,
  FiZap,
  FiX,
  FiRefreshCw,
  FiMap,
  FiBook,
  FiAward,
  FiCheck,
  FiAlertCircle,
  FiBookOpen,
  FiMoon,
  FiSun,
} from 'react-icons/fi';
import Sidebar from '../../../Sidebar';
import {
  API_BASE_URL,
  getAuthHeaders,
  redirectToLoginWithReturn,
} from '../../../../utils/utils';
import { markLessonCompleted } from '../../../../utils/lessonCompletion';
import { sanitizeHtml } from '../../../../utils/safeHtml';
import IELTSCourseCopilotPanel from './IELTSCourseCopilotPanel';
import ListeningPassagePlayer from './ListeningPassagePlayer';
import { getListeningPassageForCourse } from './data/skills/listening/listeningPassages';
import {
  buildEmptyExerciseAnswers,
  buildIeltsCourseStorageKey,
  evaluateQuestionAnswer,
  formatAcceptedAnswersForDisplay,
  getDeterministicShuffledOptions,
  isQuestionAnswered,
  isScorableExerciseType,
  mergeExerciseAnswersFromStorage,
  pickExerciseKeyedFlags,
} from './ieltsCourseUtils';
import './IELTSCourse.css';

const COURSE_PROGRESS_STORAGE_VERSION = 2;
const COURSE_THEME_STORAGE_KEY = 'liwaza_ielts_course_theme';
const COPILOT_COMPACT_MEDIA_QUERY = '(max-width: 1100px)';

const DIFFICULTY_LABELS = {
  1: { label: 'A1-A2', color: '#22c55e' },
  2: { label: 'A2-B1', color: '#3b82f6' },
  3: { label: 'B1-B2', color: '#f59e0b' },
  4: { label: 'B2-C1', color: '#f97316' },
  5: { label: 'C1-C2', color: '#ef4444' },
};

const stripHtml = (value) =>
  String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export default function IELTSCourse({
  courseId,
  courseType,
  title,
  icon: IconComponent,
  overview = {},
  sections = [],
  exercises = [],
  tips = [],
  dailyPractice = [],
  backPath = '/exams/english',
  backLabel = 'Back to Course',
  aiContext = '',
  lessonId = null,
  difficulty = null,
  onLessonComplete = null,
  listeningPassage: listeningPassageProp = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const contentRef = useRef(null);
  const hasHydratedRef = useRef(false);
  const journeyActivityId = location.state?.journeyActivityId || null;

  const [sectionsRead, setSectionsRead] = useState({});
  const [exerciseAnswers, setExerciseAnswers] = useState({});
  const [exerciseResults, setExerciseResults] = useState({});
  const [showExplanations, setShowExplanations] = useState({});
  const [isLessonComplete, setIsLessonComplete] = useState(false);
  const [hasExplicitlyFinished, setHasExplicitlyFinished] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [visitedStepIndices, setVisitedStepIndices] = useState({ 0: true });
  const [copilotOpen, setCopilotOpen] = useState(() => {
    if (typeof window === 'undefined') {
      return true;
    }

    return !window.matchMedia(COPILOT_COMPACT_MEDIA_QUERY).matches;
  });
  const [isCompactViewport, setIsCompactViewport] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.matchMedia(COPILOT_COMPACT_MEDIA_QUERY).matches;
  });
  const [copilotMessages, setCopilotMessages] = useState([]);
  const [copilotGenerating, setCopilotGenerating] = useState(false);
  const [activeContext, setActiveContext] = useState(title);
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') {
      return 'light';
    }

    try {
      const savedTheme = window.localStorage.getItem(COURSE_THEME_STORAGE_KEY);
      if (savedTheme === 'dark' || savedTheme === 'light') {
        return savedTheme;
      }
    } catch {
      // Ignore storage access issues and fall back safely.
    }

    if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }

    return 'light';
  });

  const isDarkMode = theme === 'dark';
  const listeningPassage =
    listeningPassageProp || getListeningPassageForCourse(courseId);
  const [listeningAnswers, setListeningAnswers] = useState({});
  const [listeningChecked, setListeningChecked] = useState(false);

  const allSteps = useMemo(() => {
    const steps = [];
    if (listeningPassage) {
      steps.push({
        type: 'listeningPassage',
        title: listeningPassage.title || 'Listen and answer',
        data: listeningPassage,
      });
    }

    sections.forEach((section, idx) => {
      steps.push({
        type: 'section',
        index: idx,
        title: section.title,
        data: section,
      });
    });

    exercises.forEach((exercise, idx) => {
      steps.push({
        type: 'exercise',
        index: idx,
        title: exercise.title,
        data: exercise,
      });
    });

    if (tips.length > 0) {
      steps.push({ type: 'tips', title: 'Tips to improve', data: tips });
    }

    if (dailyPractice.length > 0) {
      steps.push({
        type: 'dailyPractice',
        title: 'Daily practice',
        data: dailyPractice,
      });
    }

    return steps;
  }, [dailyPractice, exercises, listeningPassage, sections, tips]);

  const storageKey = useMemo(
    () =>
      buildIeltsCourseStorageKey({
        courseType,
        courseId,
        lessonId,
        journeyActivityId,
        version: COURSE_PROGRESS_STORAGE_VERSION,
      }),
    [courseId, courseType, journeyActivityId, lessonId]
  );

  const optionOrders = useMemo(() => {
    const orders = {};

    exercises.forEach((exercise, exerciseIdx) => {
      exercise.questions?.forEach((question, questionIdx) => {
        if (!Array.isArray(question.options)) {
          return;
        }

        orders[`exercise-${exerciseIdx}-question-${questionIdx}`] =
          getDeterministicShuffledOptions({
            courseId,
            exerciseIdx,
            questionIdx,
            options: question.options,
          });
      });
    });

    return orders;
  }, [courseId, exercises]);

  useLayoutEffect(() => {
    hasHydratedRef.current = false;

    let savedState = null;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        savedState = JSON.parse(raw);
      }
    } catch {
      savedState = null;
    }

    if (savedState && savedState.version === COURSE_PROGRESS_STORAGE_VERSION) {
      const maxStep = Math.max(0, allSteps.length - 1);
      setCurrentStep(
        typeof savedState.currentStep === 'number'
          ? Math.min(Math.max(savedState.currentStep, 0), maxStep)
          : 0
      );
      setSectionsRead(
        savedState.sectionsRead && typeof savedState.sectionsRead === 'object'
          ? savedState.sectionsRead
          : {}
      );
      setExerciseAnswers(
        mergeExerciseAnswersFromStorage(savedState.exerciseAnswers, exercises)
      );
      setExerciseResults(
        pickExerciseKeyedFlags(savedState.exerciseResults, exercises)
      );
      setShowExplanations(
        pickExerciseKeyedFlags(savedState.showExplanations, exercises)
      );
      setVisitedStepIndices(
        savedState.visitedStepIndices &&
          typeof savedState.visitedStepIndices === 'object'
          ? { 0: true, ...savedState.visitedStepIndices }
          : { 0: true }
      );
      setHasExplicitlyFinished(Boolean(savedState.hasExplicitlyFinished));
      setIsLessonComplete(Boolean(savedState.isLessonComplete));
    } else {
      setCurrentStep(0);
      setSectionsRead({});
      setExerciseAnswers(buildEmptyExerciseAnswers(exercises));
      setExerciseResults({});
      setShowExplanations({});
      setVisitedStepIndices({ 0: true });
      setHasExplicitlyFinished(false);
      setIsLessonComplete(false);
    }

    hasHydratedRef.current = true;
  }, [allSteps.length, exercises, storageKey]);

  useEffect(() => {
    if (!hasHydratedRef.current) {
      return;
    }

    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          version: COURSE_PROGRESS_STORAGE_VERSION,
          currentStep,
          sectionsRead,
          exerciseAnswers,
          exerciseResults,
          showExplanations,
          visitedStepIndices,
          hasExplicitlyFinished,
          isLessonComplete,
        })
      );
    } catch (error) {
      console.warn('Unable to persist IELTS course progress.', error);
    }
  }, [
    currentStep,
    exerciseAnswers,
    exerciseResults,
    hasExplicitlyFinished,
    isLessonComplete,
    sectionsRead,
    showExplanations,
    storageKey,
    visitedStepIndices,
  ]);

  useEffect(() => {
    setVisitedStepIndices((prev) => ({ ...prev, [currentStep]: true }));
  }, [currentStep]);

  useEffect(() => {
    setCopilotMessages([
      {
        role: 'ai',
        content: `I'm here to help you with **${title}**. Ask me to explain the current step, give more examples, or quiz you on the lesson.`,
      },
    ]);
  }, [title]);

  useEffect(() => {
    setActiveContext(allSteps[currentStep]?.title || title);
  }, [allSteps, currentStep, title]);

  const totalSections = sections.length;
  const readSections = Object.values(sectionsRead).filter(Boolean).length;
  const totalExercises = exercises.length;
  const completedExercises =
    Object.values(exerciseResults).filter(Boolean).length;

  const totalProgress = useMemo(() => {
    if (isLessonComplete) {
      return 100;
    }

    if (allSteps.length === 0) {
      return 100;
    }

    let completedSteps = 0;

    allSteps.forEach((step, idx) => {
      if (step.type === 'section' && sectionsRead[`section-${step.index}`]) {
        completedSteps += 1;
      }

      if (
        step.type === 'exercise' &&
        exerciseResults[`exercise-${step.index}`]
      ) {
        completedSteps += 1;
      }

      if (
        (step.type === 'tips' || step.type === 'dailyPractice') &&
        visitedStepIndices[idx]
      ) {
        completedSteps += 1;
      }
    });

    return Math.round((completedSteps / allSteps.length) * 100);
  }, [
    allSteps,
    exerciseResults,
    isLessonComplete,
    sectionsRead,
    visitedStepIndices,
  ]);

  const completionSummary = useMemo(() => {
    const allSectionsRead =
      totalSections === 0 || readSections >= totalSections;
    const allExercisesDone =
      totalExercises === 0 || completedExercises >= totalExercises;
    const supplementaryStepsVisited = allSteps.every((step, idx) => {
      if (step.type !== 'tips' && step.type !== 'dailyPractice') {
        return true;
      }

      return Boolean(visitedStepIndices[idx]);
    });

    return {
      allSectionsRead,
      allExercisesDone,
      supplementaryStepsVisited,
      completionReady:
        allSectionsRead &&
        allExercisesDone &&
        supplementaryStepsVisited &&
        hasExplicitlyFinished,
    };
  }, [
    allSteps,
    completedExercises,
    hasExplicitlyFinished,
    readSections,
    totalExercises,
    totalSections,
    visitedStepIndices,
  ]);

  useEffect(() => {
    if (!isLessonComplete && completionSummary.completionReady) {
      setIsLessonComplete(true);
      markLessonCompleted(courseType, courseId, journeyActivityId);
      if (typeof onLessonComplete === 'function') {
        onLessonComplete({ courseId, courseType });
      }
    }
  }, [
    completionSummary.completionReady,
    courseId,
    courseType,
    isLessonComplete,
    journeyActivityId,
    onLessonComplete,
  ]);

  useEffect(() => {
    try {
      window.localStorage.setItem(COURSE_THEME_STORAGE_KEY, theme);
    } catch {
      // Ignore storage access issues.
    }
  }, [theme]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia(COPILOT_COMPACT_MEDIA_QUERY);

    const syncCopilotViewport = (event) => {
      const matches = event.matches;
      setIsCompactViewport(matches);
      setCopilotOpen(!matches);
    };

    syncCopilotViewport(mediaQuery);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', syncCopilotViewport);
      return () => mediaQuery.removeEventListener('change', syncCopilotViewport);
    }

    mediaQuery.addListener(syncCopilotViewport);
    return () => mediaQuery.removeListener(syncCopilotViewport);
  }, []);

  const handleAnswerChange = (exerciseIdx, questionIdx, value) => {
    setExerciseAnswers((prev) => ({
      ...prev,
      [`exercise-${exerciseIdx}`]:
        prev[`exercise-${exerciseIdx}`]?.map((ans, i) =>
          i === questionIdx ? value : ans
        ) || [],
    }));
  };

  const canCheckExercise = useCallback(
    (exerciseIdx) => {
      const exercise = exercises[exerciseIdx];
      if (!exercise) {
        return false;
      }

      return exercise.questions.every((_, questionIdx) =>
        isQuestionAnswered(
          exerciseAnswers[`exercise-${exerciseIdx}`]?.[questionIdx]
        )
      );
    },
    [exerciseAnswers, exercises]
  );

  const checkExercise = (exerciseIdx) => {
    if (!canCheckExercise(exerciseIdx)) {
      return;
    }

    setExerciseResults((prev) => ({
      ...prev,
      [`exercise-${exerciseIdx}`]: true,
    }));
    setShowExplanations((prev) => ({
      ...prev,
      [`exercise-${exerciseIdx}`]: true,
    }));
  };

  const resetExercise = (exerciseIdx) => {
    const exercise = exercises[exerciseIdx];
    setExerciseAnswers((prev) => ({
      ...prev,
      [`exercise-${exerciseIdx}`]: exercise.questions.map(() => ''),
    }));
    setExerciseResults((prev) => ({
      ...prev,
      [`exercise-${exerciseIdx}`]: false,
    }));
    setShowExplanations((prev) => ({
      ...prev,
      [`exercise-${exerciseIdx}`]: false,
    }));
  };

  const toggleExplanations = (exerciseIdx) => {
    setShowExplanations((prev) => ({
      ...prev,
      [`exercise-${exerciseIdx}`]: !prev[`exercise-${exerciseIdx}`],
    }));
  };

  const getQuestionEvaluation = useCallback(
    (exerciseIdx, questionIdx) =>
      evaluateQuestionAnswer({
        exercise: exercises[exerciseIdx],
        question: exercises[exerciseIdx]?.questions?.[questionIdx],
        userAnswer: exerciseAnswers[`exercise-${exerciseIdx}`]?.[questionIdx],
      }),
    [exerciseAnswers, exercises]
  );

  const getExerciseScore = (exerciseIdx) => {
    const exercise = exercises[exerciseIdx];
    if (!exercise || !exerciseResults[`exercise-${exerciseIdx}`]) return null;

    let correct = 0;
    let total = 0;
    let pendingAiReviewCount = 0;
    exercise.questions.forEach((_, qIdx) => {
      const evaluation = getQuestionEvaluation(exerciseIdx, qIdx);
      if (evaluation.needsAiReview) {
        pendingAiReviewCount += 1;
      }
      if (evaluation.countsTowardScore) {
        total += 1;
        if (evaluation.isCorrect) {
          correct += 1;
        }
      }
    });

    if (total === 0 && pendingAiReviewCount === 0) {
      return null;
    }

    return {
      correct,
      pendingAiReviewCount,
      percentage: total > 0 ? Math.round((correct / total) * 100) : null,
      total,
    };
  };

  const renderQuestionText = (text) => (
    <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(text || '') }} />
  );

  const buildLessonSnippetForCopilot = useCallback(() => {
    const current = allSteps[currentStep];
    if (!current) {
      return '';
    }

    const snippets = [current.title];

    if (current.type === 'section') {
      snippets.push(stripHtml(current.data.content));
      (current.data.points || []).forEach((point) =>
        snippets.push(stripHtml(point))
      );
      (current.data.examples || []).forEach((example) => {
        snippets.push(example.label);
        snippets.push(example.text);
      });
      snippets.push(stripHtml(current.data.warning));
    }

    if (current.type === 'exercise') {
      snippets.push(current.data.instructions);
      (current.data.questions || []).forEach((question) => {
        snippets.push(stripHtml(question.text));
        (question.options || []).forEach((option) => snippets.push(option));
        snippets.push(stripHtml(question.explanation));
      });
    }

    if (current.type === 'tips' || current.type === 'dailyPractice') {
      (current.data || []).forEach((entry) => snippets.push(stripHtml(entry)));
    }

    return snippets.filter(Boolean).join('\n').slice(0, 4000);
  }, [allSteps, currentStep]);

  const handleCopilotSend = useCallback(
    async (prompt) => {
      const trimmedPrompt = String(prompt || '').trim();
      if (!trimmedPrompt) {
        return;
      }

      let storedUser = null;
      try {
        storedUser = JSON.parse(sessionStorage.getItem('user') || 'null');
      } catch (error) {
        console.error('Unable to read authenticated user for IELTS copilot:', error);
      }

      const userEmail = storedUser?.email;
      if (!userEmail) {
        redirectToLoginWithReturn({
          navigate,
          message: 'Votre session a expire. Veuillez vous reconnecter.',
          publishLogoutReason: 'session_expired',
        });
        return;
      }

      setCopilotMessages((prev) => [
        ...prev,
        { role: 'user', content: trimmedPrompt },
      ]);
      setCopilotGenerating(true);

      try {
        const userName =
          [storedUser?.first_name, storedUser?.last_name].filter(Boolean).join(' ') ||
          storedUser?.name ||
          '';
        const lessonContext = {
          course_id: courseId,
          course_title: title,
          course_type: courseType,
          lesson_id: lessonId || courseId,
          active_step: activeContext,
          lesson_snippet: buildLessonSnippetForCopilot(),
          ai_context: aiContext || title,
        };

        const response = await fetch(`${API_BASE_URL}/api/copilot-message`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            message: trimmedPrompt,
            conversation_history: copilotMessages.map((message) => ({
              role: message.role === 'ai' ? 'assistant' : 'user',
              content: message.content,
            })),
            userEmail,
            user_context: {
              user_email: userEmail,
              user_name: userName,
              user_role: storedUser?.role?.name || storedUser?.role || 'student',
              current_page: `/exams/english/courses/${lessonId || courseId || 'lesson'}`,
              priority_school: 'ielts_course',
            },
            session_context: {
              ...lessonContext,
              has_files: false,
            },
            tools_available: ['test_prep_guidance'],
          }),
        });

        if (response.status === 401) {
          redirectToLoginWithReturn({
            navigate,
            message: 'Votre session a expire. Veuillez vous reconnecter.',
            publishLogoutReason: 'session_expired',
          });
          return;
        }

        if (!response.ok) {
          throw new Error(`Copilot request failed (${response.status})`);
        }

        const data = await response.json();
        setCopilotMessages((prev) => [
          ...prev,
          {
            role: 'ai',
            content:
              data.message ||
              data.response ||
              "I couldn't generate a helpful answer just now. Please try again.",
          },
        ]);
      } catch (error) {
        console.error('IELTS lesson copilot failed:', error);
        setCopilotMessages((prev) => [
          ...prev,
          {
            role: 'ai',
            content:
              "I'm having trouble connecting right now. Try again in a moment.",
          },
        ]);
      } finally {
        setCopilotGenerating(false);
      }
    },
    [
      activeContext,
      aiContext,
      buildLessonSnippetForCopilot,
      copilotMessages,
      courseId,
      courseType,
      lessonId,
      title,
    ]
  );

  const buildAiReviewPrompt = useCallback(
    (exercise, question, userAnswer, evaluation) => {
      const acceptedAnswers = formatAcceptedAnswersForDisplay(
        exercise,
        question
      );
      const reviewFocus = [
        ...(exercise?.aiReviewFocus || []),
        ...(question?.aiReviewFocus || []),
      ].filter(Boolean);

      if (
        question?.evaluationMode === 'ai-review' ||
        exercise?.type === 'writing'
      ) {
        return [
          'Act as an IELTS coach and review this learner response.',
          `Prompt: "${question.text}"`,
          userAnswer
            ? `Learner answer: "${userAnswer}"`
            : 'Learner answer: [no draft provided]',
          acceptedAnswers ? `Reference answer(s): ${acceptedAnswers}` : null,
          reviewFocus.length
            ? `Focus on: ${reviewFocus.join(', ')}.`
            : 'Focus on task response, clarity, IELTS style, and one concrete improvement.',
          'Give concise feedback and suggest a stronger model answer.',
        ]
          .filter(Boolean)
          .join('\n');
      }

      if (evaluation?.needsAiReview) {
        return [
          'Check whether this IELTS answer should still be accepted even if the wording differs.',
          `Question: "${question.text}"`,
          `Learner answer: "${userAnswer}"`,
          acceptedAnswers
            ? `Stored accepted answer(s): ${acceptedAnswers}`
            : null,
          reviewFocus.length
            ? `Consider these criteria: ${reviewFocus.join(', ')}.`
            : 'Decide whether the learner captured the required meaning, then explain your reasoning.',
          'Reply with: verdict, short reason, and one better answer if needed.',
        ]
          .filter(Boolean)
          .join('\n');
      }

      return [
        `I got this IELTS question wrong: "${question.text}"`,
        userAnswer ? `My answer was "${userAnswer}".` : null,
        acceptedAnswers ? `The accepted answer is "${acceptedAnswers}".` : null,
        'Please explain the pattern and give one more similar example.',
      ]
        .filter(Boolean)
        .join(' ');
    },
    []
  );

  const currentStepData = allSteps[currentStep] || null;
  const totalSteps = allSteps.length;
  const isLastStep = currentStep >= totalSteps - 1;

  const canAdvanceFromCurrentStep = useMemo(() => {
    if (!currentStepData) {
      return false;
    }

    if (currentStepData.type === 'exercise') {
      return Boolean(exerciseResults[`exercise-${currentStepData.index}`]);
    }

    return true;
  }, [currentStepData, exerciseResults]);

  const canFinishLesson = useMemo(() => {
    if (!isLastStep) {
      return true;
    }

    if (!currentStepData) {
      return false;
    }

    if (currentStepData.type === 'exercise' && !canAdvanceFromCurrentStep) {
      return false;
    }

    return (
      completionSummary.allExercisesDone &&
      completionSummary.supplementaryStepsVisited &&
      (completionSummary.allSectionsRead || currentStepData.type === 'section')
    );
  }, [
    canAdvanceFromCurrentStep,
    completionSummary.allExercisesDone,
    completionSummary.allSectionsRead,
    completionSummary.supplementaryStepsVisited,
    currentStepData,
    isLastStep,
  ]);

  const moveToStep = (targetStep) => {
    if (!currentStepData) {
      setCurrentStep(targetStep);
      return;
    }

    if (currentStepData.type === 'section') {
      setSectionsRead((prev) => ({
        ...prev,
        [`section-${currentStepData.index}`]: true,
      }));
    }

    setCurrentStep(targetStep);
  };

  const handlePrimaryStepAction = () => {
    if (!currentStepData) {
      return;
    }

    if (currentStepData.type === 'section') {
      setSectionsRead((prev) => ({
        ...prev,
        [`section-${currentStepData.index}`]: true,
      }));
    }

    if (isLastStep) {
      setHasExplicitlyFinished(true);
      return;
    }

    setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
  };

  const goBack = () => {
    try {
      if (window.history.length > 1) {
        navigate(-1);
        return;
      }
    } catch {
      // Ignore history access issues and fall back to the configured path.
    }

    navigate(backPath);
  };

  const renderExerciseInput = (
    exercise,
    exerciseIdx,
    question,
    questionIdx
  ) => {
    const isChecked = exerciseResults[`exercise-${exerciseIdx}`];
    const userAnswer =
      exerciseAnswers[`exercise-${exerciseIdx}`]?.[questionIdx] || '';
    const evaluation = getQuestionEvaluation(exerciseIdx, questionIdx);
    const isCorrect = evaluation.isCorrect;
    const needsAiReview = evaluation.needsAiReview;
    const showExp = showExplanations[`exercise-${exerciseIdx}`];
    const optionOrder =
      optionOrders[`exercise-${exerciseIdx}-question-${questionIdx}`] || [];
    const acceptedAnswerLabel = formatAcceptedAnswersForDisplay(
      exercise,
      question
    );
    const fillInStatusClass = !isChecked
      ? ''
      : needsAiReview
        ? 'ielts-course-needs-review'
        : isCorrect
          ? 'ielts-course-correct'
          : 'ielts-course-incorrect';

    switch (exercise.type) {
      case 'multiple-choice':
      case 'select':
        return (
          <div className="ielts-course-exercise-question" key={questionIdx}>
            <p className="ielts-course-question-text">
              {renderQuestionText(question.text)}
            </p>
            <div className="ielts-course-options-grid">
              {optionOrder.map((option, optIdx) => (
                <label
                  key={optIdx}
                  className={`ielts-course-option-label ${
                    isChecked
                      ? option === question.answer
                        ? 'ielts-course-correct'
                        : userAnswer === option
                          ? 'ielts-course-incorrect'
                          : ''
                      : userAnswer === option
                        ? 'ielts-course-selected'
                        : ''
                  }`}
                >
                  <input
                    type="radio"
                    name={`q-${exerciseIdx}-${questionIdx}`}
                    value={option}
                    checked={userAnswer === option}
                    onChange={(e) =>
                      handleAnswerChange(
                        exerciseIdx,
                        questionIdx,
                        e.target.value
                      )
                    }
                    disabled={isChecked}
                  />
                  <span className="ielts-course-option-text">{option}</span>
                  {isChecked && option === question.answer && (
                    <FiCheckCircle className="ielts-course-option-icon ielts-course-correct" />
                  )}
                  {isChecked &&
                    userAnswer === option &&
                    option !== question.answer && (
                      <FiX className="ielts-course-option-icon ielts-course-incorrect" />
                    )}
                </label>
              ))}
            </div>
            {isChecked && showExp && question.explanation && (
              <div className="ielts-course-answer-explanation">
                <FiAlertCircle className="ielts-course-exp-icon" />
                <span>{renderQuestionText(question.explanation)}</span>
              </div>
            )}
            {isChecked && !isCorrect && (
              <div className="ielts-course-answer-actions">
                <button
                  type="button"
                  className="ielts-course-inline-ai-btn ielts-course-inline-ai-btn--answer"
                  onClick={() =>
                    handleCopilotSend(
                      buildAiReviewPrompt(
                        exercise,
                        question,
                        userAnswer,
                        evaluation
                      )
                    )
                  }
                >
                  <FiHelpCircle /> Explain this answer
                </button>
              </div>
            )}
          </div>
        );

      case 'fill-in':
        return (
          <div className="ielts-course-exercise-question" key={questionIdx}>
            <p className="ielts-course-question-text">
              {renderQuestionText(question.text)}
            </p>
            <div className="ielts-course-fill-in-container">
              <input
                type="text"
                className={`ielts-course-fill-in-input ${fillInStatusClass}`}
                value={userAnswer}
                onChange={(e) =>
                  handleAnswerChange(exerciseIdx, questionIdx, e.target.value)
                }
                placeholder="Type your answer..."
                disabled={isChecked}
              />
              {isChecked && (
                <span
                  className={`ielts-course-result-icon ${fillInStatusClass}`}
                >
                  {needsAiReview ? (
                    <FiAlertCircle />
                  ) : isCorrect ? (
                    <FiCheckCircle />
                  ) : (
                    <FiX />
                  )}
                </span>
              )}
            </div>
            {isChecked && showExp && (
              <div className="ielts-course-answer-explanation">
                {acceptedAnswerLabel && (
                  <>
                    <strong>
                      Accepted answer
                      {evaluation.acceptedAnswers.length > 1 ? 's' : ''}:
                    </strong>{' '}
                    {acceptedAnswerLabel}
                  </>
                )}
                {question.explanation && <p>{question.explanation}</p>}
                {needsAiReview && (
                  <p>
                    Your wording may still be valid. Ask AI to compare the
                    meaning with the stored answer before you move on.
                  </p>
                )}
              </div>
            )}
            {isChecked && (!isCorrect || needsAiReview) && (
              <div className="ielts-course-answer-actions">
                <button
                  type="button"
                  className="ielts-course-inline-ai-btn ielts-course-inline-ai-btn--answer"
                  onClick={() =>
                    handleCopilotSend(
                      buildAiReviewPrompt(
                        exercise,
                        question,
                        userAnswer,
                        evaluation
                      )
                    )
                  }
                >
                  <FiHelpCircle />{' '}
                  {needsAiReview
                    ? 'Ask AI to review this answer'
                    : 'Explain this answer'}
                </button>
              </div>
            )}
          </div>
        );

      case 'writing':
        return (
          <div
            className="ielts-course-exercise-question ielts-course-writing"
            key={questionIdx}
          >
            <p className="ielts-course-question-text">
              {renderQuestionText(question.text)}
            </p>
            <textarea
              className="ielts-course-writing-input"
              value={userAnswer}
              onChange={(e) =>
                handleAnswerChange(exerciseIdx, questionIdx, e.target.value)
              }
              placeholder="Write your answer..."
              disabled={isChecked}
              rows={4}
            />
            {isChecked && showExp && question.example && (
              <div className="ielts-course-answer-explanation ielts-course-example">
                <strong>Example Answer:</strong>
                <p>{question.example}</p>
                {question.tip && (
                  <p className="ielts-course-tip">
                    <FiAward /> {question.tip}
                  </p>
                )}
              </div>
            )}
            <div className="ielts-course-answer-actions">
              <button
                type="button"
                className="ielts-course-inline-ai-btn ielts-course-inline-ai-btn--answer"
                onClick={() =>
                  handleCopilotSend(
                    buildAiReviewPrompt(
                      exercise,
                      question,
                      userAnswer,
                      evaluation
                    )
                  )
                }
              >
                <FiZap /> Get AI review
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderSectionBody = (section) => (
    <>
      {section.content && (
        <div
          className="ielts-course-section-text"
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(section.content),
          }}
        />
      )}

      {section.points && (
        <ul className="ielts-course-section-points">
          {section.points.map((point, pIdx) => (
            <li key={pIdx}>
              <FiCheckCircle className="ielts-course-point-icon" />
              <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(point) }} />
            </li>
          ))}
        </ul>
      )}

      {section.examples && (
        <div className="ielts-course-section-examples">
          {section.examples.map((example, eIdx) => (
            <div key={eIdx} className="ielts-course-example-box">
              {example.label && (
                <span className="ielts-course-example-label">
                  {example.label}
                </span>
              )}
              <p className="ielts-course-example-text">{example.text}</p>
              {example.translation && (
                <p className="ielts-course-example-translation">
                  {example.translation}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {section.warning && (
        <div className="ielts-course-section-warning">
          <FiAlertCircle />
          <span>{section.warning}</span>
        </div>
      )}

      {section.table && (
        <div className="ielts-course-section-table-wrapper">
          <table className="ielts-course-section-table">
            <thead>
              <tr>
                {section.table.headers.map((header, hIdx) => (
                  <th key={hIdx}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.table.rows.map((row, rIdx) => (
                <tr key={rIdx}>
                  {row.map((cell, cIdx) => (
                    <td key={cIdx}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  const renderExerciseBlock = (exercise, exerciseIdx) => {
    const score = getExerciseScore(exerciseIdx);
    const isChecked = exerciseResults[`exercise-${exerciseIdx}`];
    const canCheck = canCheckExercise(exerciseIdx);
    const isScorableExercise = isScorableExerciseType(exercise.type);

    return (
      <div key={exerciseIdx} className="ielts-course-exercise-block">
        <div className="ielts-course-exercise-header">
          <div>
            <h3>
              <span className="ielts-course-exercise-number">
                {exerciseIdx + 1}
              </span>
              {exercise.title}
            </h3>
            {(exercise.difficulty || exercise.bandLevel) && (
              <div className="ielts-course-exercise-meta">
                {exercise.difficulty && (
                  <span className="ielts-course-exercise-chip">
                    {exercise.difficulty}
                  </span>
                )}
                {exercise.bandLevel && (
                  <span className="ielts-course-exercise-chip">
                    {exercise.bandLevel}
                  </span>
                )}
              </div>
            )}
          </div>
          {score && (
            <span
              className={`ielts-course-exercise-score ${
                score.total === 0
                  ? 'ielts-course-good'
                  : score.percentage >= 80
                    ? 'ielts-course-excellent'
                    : score.percentage >= 50
                      ? 'ielts-course-good'
                      : 'ielts-course-needs-work'
              }`}
            >
              {score.total > 0
                ? `${score.correct}/${score.total} (${score.percentage}%)`
                : `${score.pendingAiReviewCount} AI review`}
              {score.total > 0 && score.pendingAiReviewCount > 0
                ? ` · ${score.pendingAiReviewCount} AI review`
                : ''}
            </span>
          )}
        </div>

        {exercise.instructions && (
          <p className="ielts-course-exercise-instructions">
            {exercise.instructions}
          </p>
        )}

        {!isScorableExercise && (
          <p className="ielts-course-step-hint">
            This is a practice-and-feedback exercise, so use the AI review for
            coaching rather than a right-or-wrong score.
          </p>
        )}

        <div className="ielts-course-exercise-questions">
          {exercise.questions.map((question, qIdx) =>
            renderExerciseInput(exercise, exerciseIdx, question, qIdx)
          )}
        </div>

        {!isChecked && !canCheck && (
          <p className="ielts-course-step-hint">
            Answer every prompt before checking this exercise.
          </p>
        )}

        <div className="ielts-course-exercise-actions">
          {!isChecked ? (
            <button
              type="button"
              className="ielts-course-btn-check-answers"
              onClick={() => checkExercise(exerciseIdx)}
              disabled={!canCheck}
            >
              <FiPlay /> Check Answers
            </button>
          ) : (
            <>
              <button
                type="button"
                className="ielts-course-btn-reset"
                onClick={() => resetExercise(exerciseIdx)}
              >
                <FiRefreshCw /> Try Again
              </button>
              <button
                type="button"
                className="ielts-course-btn-explanations"
                onClick={() => toggleExplanations(exerciseIdx)}
              >
                {showExplanations[`exercise-${exerciseIdx}`]
                  ? 'Hide Explanations'
                  : 'Show Explanations'}
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    if (!currentStepData) {
      return null;
    }

    if (currentStepData.type === 'listeningPassage') {
      return (
        <div className="ielts-step-card">
          <div className="ielts-step-card__header">
            <div className="ielts-step-card__title">
              <h3>{currentStepData.title}</h3>
            </div>
          </div>
          <div className="ielts-step-card__body">
            <ListeningPassagePlayer
              passage={currentStepData.data}
              answers={listeningAnswers}
              showResults={listeningChecked}
              onAnswer={(idx, option) =>
                setListeningAnswers((prev) => ({ ...prev, [idx]: option }))
              }
            />
            {(currentStepData.data.questions || []).length > 0 && (
              <button
                type="button"
                className="ielts-course-inline-ai-btn"
                style={{ marginTop: '0.75rem' }}
                onClick={() => setListeningChecked(true)}
              >
                Check listening answers
              </button>
            )}
          </div>
        </div>
      );
    }

    if (currentStepData.type === 'section') {
      return (
        <div className="ielts-step-card">
          <div className="ielts-step-card__header">
            <div className="ielts-step-card__title">
              <span className="ielts-course-section-number">
                {currentStepData.index + 1}
              </span>
              <h3>{currentStepData.title}</h3>
            </div>
            <button
              type="button"
              className="ielts-course-inline-ai-btn"
              onClick={() =>
                handleCopilotSend(
                  `Explain the IELTS lesson section "${currentStepData.title}" in a simpler way and add one more example.`
                )
              }
            >
              <FiZap /> Ask AI
            </button>
          </div>
          <div className="ielts-step-card__body">
            {renderSectionBody(currentStepData.data)}
          </div>
        </div>
      );
    }

    if (currentStepData.type === 'exercise') {
      return (
        <div className="ielts-step-card">
          {renderExerciseBlock(currentStepData.data, currentStepData.index)}
        </div>
      );
    }

    if (currentStepData.type === 'tips') {
      return (
        <div className="ielts-step-card">
          <div className="ielts-step-card__header">
            <div className="ielts-step-card__title">
              <FiAward className="ielts-step-card__icon" />
              <h3>Tips to improve</h3>
            </div>
          </div>
          <div className="ielts-step-card__body">
            <div className="ielts-course-tips ielts-course-meta-card">
              <ul>
                {tips.map((tip, idx) => (
                  <li key={idx}>
                    <FiCheckCircle />
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      );
    }

    if (currentStepData.type === 'dailyPractice') {
      return (
        <div className="ielts-step-card">
          <div className="ielts-step-card__header">
            <div className="ielts-step-card__title">
              <FiBook className="ielts-step-card__icon" />
              <h3>Daily practice</h3>
            </div>
          </div>
          <div className="ielts-step-card__body">
            <div className="ielts-course-daily ielts-course-meta-card">
              <ul>
                {dailyPractice.map((practice, idx) => (
                  <li key={idx}>
                    <FiArrowRight />
                    <span>{practice}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div
      className={`english-layout ielts-course-shell ${
        isDarkMode ? 'ielts-course-shell--dark' : ''
      }`}
    >
      <Sidebar />
      <div
        className={`ielts-course-page ${
          copilotOpen && !isCompactViewport
            ? 'ielts-course-page--with-copilot'
            : ''
        }`}
      >
        <main className="english-main ielts-course-main" ref={contentRef}>
          <div className="ielts-course-sticky-header">
            <div className="ielts-course-sticky-header__top">
              <button
                type="button"
                className="ielts-course-back-btn"
                onClick={goBack}
              >
                <FiArrowLeft /> Back
              </button>

              <div className="ielts-course-progress-bar ielts-course-progress-bar--inline">
                <div className="ielts-course-progress-info">
                  <span className="ielts-course-progress-label">
                    <FiBookOpen /> Guided lesson progress
                  </span>
                </div>
                <div className="ielts-course-progress-track">
                  <div
                    className="ielts-course-progress-fill"
                    style={{ width: `${totalProgress}%` }}
                  />
                </div>
                <div className="ielts-course-progress-details">
                  <span>
                    <FiBook /> {readSections}/{totalSections} sections completed
                  </span>
                  <span>
                    <FiEdit3 /> {completedExercises}/{totalExercises} exercises
                    checked
                  </span>
                </div>
              </div>

              <div className="ielts-course-sticky-header__actions">
                <button
                  type="button"
                  className={`ielts-course-theme-toggle ielts-course-copilot-toggle ${
                    copilotOpen ? 'ielts-course-copilot-toggle--active' : ''
                  }`}
                  onClick={() => setCopilotOpen((prev) => !prev)}
                  aria-label={copilotOpen ? 'Hide lesson help' : 'Show lesson help'}
                  title={copilotOpen ? 'Hide lesson help' : 'Show lesson help'}
                >
                  <FiZap size={15} />
                  <span>{copilotOpen ? 'Hide help' : 'Lesson help'}</span>
                </button>
                <button
                  type="button"
                  className="ielts-course-theme-toggle"
                  onClick={() =>
                    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))
                  }
                  aria-label={
                    isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'
                  }
                  title={
                    isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'
                  }
                >
                  {isDarkMode ? <FiSun size={15} /> : <FiMoon size={15} />}
                  <span>{isDarkMode ? 'Light mode' : 'Dark mode'}</span>
                </button>
              </div>

            </div>

            <div className="ielts-step-indicator">
              <span className="ielts-step-indicator__label">
                Step {Math.min(currentStep + 1, Math.max(totalSteps, 1))} of{' '}
                {Math.max(totalSteps, 1)}
              </span>
              <div className="ielts-step-indicator__dots">
                {allSteps.map((step, idx) => (
                  <button
                    key={`${step.type}-${idx}`}
                    type="button"
                    className={`ielts-step-indicator__dot ${
                      idx === currentStep
                        ? 'ielts-step-indicator__dot--active'
                        : ''
                    } ${
                      idx < currentStep ? 'ielts-step-indicator__dot--done' : ''
                    }`}
                    onClick={() => moveToStep(idx)}
                    aria-label={`Go to step ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {(currentStep === 0 || totalSteps === 0) && (
            <div className="ielts-course-header">
              <h1 className="ielts-course-title">
                {IconComponent && (
                  <IconComponent className="ielts-course-icon" />
                )}
                {title}
                {difficulty && DIFFICULTY_LABELS[difficulty] && (
                  <span
                    className="ielts-course-difficulty-badge"
                    style={{
                      backgroundColor: `${DIFFICULTY_LABELS[difficulty].color}15`,
                      color: DIFFICULTY_LABELS[difficulty].color,
                      border: `1px solid ${DIFFICULTY_LABELS[difficulty].color}30`,
                    }}
                  >
                    CEFR {DIFFICULTY_LABELS[difficulty].label} • Difficulty{' '}
                    {difficulty}/5
                  </span>
                )}
              </h1>

              <div className="ielts-course-overview">
                {overview.impact && (
                  <div className="ielts-course-overview-card">
                    <FiTarget className="ielts-course-ov-icon" />
                    <div>
                      <span className="ielts-course-overview-label">
                        IELTS Impact
                      </span>
                      <span className="ielts-course-overview-value">
                        {overview.impact}
                      </span>
                    </div>
                  </div>
                )}
                {overview.focus && (
                  <div className="ielts-course-overview-card">
                    <FiLayers className="ielts-course-ov-icon" />
                    <div>
                      <span className="ielts-course-overview-label">
                        Key Focus
                      </span>
                      <span className="ielts-course-overview-value">
                        {overview.focus}
                      </span>
                    </div>
                  </div>
                )}
                {overview.uses && (
                  <div className="ielts-course-overview-card">
                    <FiBook className="ielts-course-ov-icon" />
                    <div>
                      <span className="ielts-course-overview-label">
                        Common Uses
                      </span>
                      <span className="ielts-course-overview-value">
                        {overview.uses}
                      </span>
                    </div>
                  </div>
                )}
                {overview.duration && (
                  <div className="ielts-course-overview-card">
                    <FiClock className="ielts-course-ov-icon" />
                    <div>
                      <span className="ielts-course-overview-label">
                        Duration
                      </span>
                      <span className="ielts-course-overview-value">
                        {overview.duration}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="ielts-course-step-content">
            {renderCurrentStep()}

            {isLastStep && !canFinishLesson && (
              <div className="ielts-course-section-warning ielts-course-step-warning">
                <FiAlertCircle />
                <span>
                  Finish becomes available once every lesson section is
                  completed and every exercise has been checked.
                </span>
              </div>
            )}

            {isLessonComplete && (
              <div className="ielts-course-complete-banner">
                <FiAward className="ielts-course-complete-icon" />
                <div>
                  <h4>Lesson Complete!</h4>
                  <p>
                    You finished the guided lesson and your progress has been
                    saved.
                  </p>
                </div>
              </div>
            )}

            <div className="ielts-step-nav">
              <button
                type="button"
                className="ielts-step-nav__btn"
                disabled={currentStep === 0}
                onClick={() => setCurrentStep((prev) => Math.max(prev - 1, 0))}
              >
                <FiArrowLeft size={14} /> Previous
              </button>

              <button
                type="button"
                className="ielts-step-nav__btn ielts-step-nav__btn--primary"
                disabled={
                  isLastStep ? !canFinishLesson : !canAdvanceFromCurrentStep
                }
                onClick={handlePrimaryStepAction}
              >
                {isLastStep ? 'Finish lesson' : 'Next'}
                {isLastStep ? (
                  <FiCheck size={14} />
                ) : (
                  <FiArrowRight size={14} />
                )}
              </button>
            </div>

            {isLastStep && isLessonComplete && (
              <div className="ielts-course-navigation">
                <button
                  type="button"
                  className="ielts-course-btn-back-to-courses"
                  onClick={() => navigate(backPath)}
                >
                  <FiArrowLeft /> {backLabel}
                </button>
                <button
                  type="button"
                  className="ielts-course-btn-my-journey"
                  onClick={() => navigate('/exams/english/journey')}
                >
                  <FiMap /> My Journey
                </button>
              </div>
            )}
          </div>
        </main>

        {copilotOpen && isCompactViewport && (
          <button
            type="button"
            className="ielts-course-copilot-backdrop"
            aria-label="Close lesson help"
            onClick={() => setCopilotOpen(false)}
          />
        )}

        {copilotOpen && (
          <IELTSCourseCopilotPanel
            activeContext={activeContext}
            courseTitle={title}
            generating={copilotGenerating}
            messages={copilotMessages}
            onClose={() => setCopilotOpen(false)}
            onSend={handleCopilotSend}
            showCloseButton={isCompactViewport}
          />
        )}
      </div>
    </div>
  );
}
